import { useRequiredContext } from "@redotech/react-util/context";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  SpecificDateOperator,
  TimeBasedMode,
  type TimeBasedCondition as TimeBasedConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import { RuleConditionType } from "@redotech/redo-model/advanced-flow/rules/types";
import { getAvailableTimezones } from "@redotech/redo-model/timezone-utils";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import type { TimeOfDay } from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import {
  RedoTimeRangePicker,
  type TimeOfDayRange,
} from "@redotech/redo-web/arbiter-components/time/redo-time-range-picker";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Link } from "@redotech/redo-web/link";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";

const modeOptions = [
  {
    id: TimeBasedMode.BUSINESS_HOURS,
    type: "text" as const,
    text: "Business hours",
    value: TimeBasedMode.BUSINESS_HOURS,
  },
  {
    id: TimeBasedMode.WEEKLY_SCHEDULE,
    type: "text" as const,
    text: "Weekly schedule",
    value: TimeBasedMode.WEEKLY_SCHEDULE,
  },
  {
    id: TimeBasedMode.SPECIFIC_DATE,
    type: "text" as const,
    text: "Specific date",
    value: TimeBasedMode.SPECIFIC_DATE,
  },
];

const specificDateOperatorOptions = [
  {
    id: SpecificDateOperator.BEFORE,
    type: "text" as const,
    text: "Before",
    value: SpecificDateOperator.BEFORE,
  },
  {
    id: SpecificDateOperator.AFTER,
    type: "text" as const,
    text: "After",
    value: SpecificDateOperator.AFTER,
  },
  {
    id: SpecificDateOperator.BETWEEN,
    type: "text" as const,
    text: "Between",
    value: SpecificDateOperator.BETWEEN,
  },
];

const DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

type DayName = (typeof DAYS)[number];

interface TimeBlock {
  start?: string;
  end?: string;
}

interface DaySchedule {
  enabled: boolean;
  timeBlocks: TimeBlock[];
}

export const TimeBasedCondition = memo(function TimeBasedCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TimeBasedConditionType;
  onChange: (updated: TimeBasedConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  // Fine-grained gating for each input inside the weekly/specific-date
  // sub-sections would be noisy. We lock the whole editor when the rule
  // binds none of the top-level fields and expose per-field checks only
  // for `mode` (the most likely single-field binding).
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const anyEditable =
    editableFields === undefined ||
    ["mode", "schedule", "timezone", "dateOperator", "dateTime"].some((f) =>
      editableFields.has(f),
    );
  const modeEditable = isEditable("mode");

  const handleModeChange = useCallback(
    (mode: TimeBasedMode) => {
      switch (mode) {
        case TimeBasedMode.BUSINESS_HOURS:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.BUSINESS_HOURS,
          });
          break;
        case TimeBasedMode.WEEKLY_SCHEDULE:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.WEEKLY_SCHEDULE,
          });
          break;
        case TimeBasedMode.SPECIFIC_DATE:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.SPECIFIC_DATE,
            dateOperator: SpecificDateOperator.AFTER,
          });
          break;
      }
    },
    [onChange],
  );

  const selectedMode = useMemo(
    () => modeOptions.find((opt) => opt.value === condition.mode),
    [condition.mode],
  );

  return (
    <Flex
      dir="column"
      gap="md"
      style={anyEditable ? undefined : { pointerEvents: "none", opacity: 0.6 }}
      w="full"
    >
      <RedoSingleSelectDropdownInput
        disabled={!modeEditable}
        options={modeOptions}
        optionSelected={({ item }) => {
          if (!modeEditable) {
            return;
          }
          handleModeChange(item.value);
        }}
        placeholder="Select mode"
        selectedItem={selectedMode}
      />
      {condition.mode === TimeBasedMode.BUSINESS_HOURS && (
        <BusinessHoursSection />
      )}
      {condition.mode === TimeBasedMode.WEEKLY_SCHEDULE && (
        <WeeklyScheduleSection condition={condition} onChange={onChange} />
      )}
      {condition.mode === TimeBasedMode.SPECIFIC_DATE && (
        <SpecificDateSection condition={condition} onChange={onChange} />
      )}
    </Flex>
  );
});

const BusinessHoursSection = memo(function BusinessHoursSection() {
  const team = useRequiredContext(TeamContext);
  const customerWidgetSupportLink = `/stores/${team._id}/settings/customer-widget?tab=support`;

  return (
    <Text fontSize="sm" textColor="secondary">
      Matches when a message is received outside of business hours.{" "}
      <Link to={customerWidgetSupportLink}>Configure business hours</Link>
    </Text>
  );
});

const TimezoneSelector = memo(function TimezoneSelector({
  timezone,
  onChange,
}: {
  timezone?: string;
  onChange: (tz: string) => void;
}) {
  const timezoneOptions = useMemo(
    () =>
      getAvailableTimezones().map((tz) => ({
        id: tz,
        type: "text" as const,
        text: tz,
        value: tz,
      })),
    [],
  );

  const selectedTimezone = useMemo(
    () => timezoneOptions.find((opt) => opt.value === timezone),
    [timezone, timezoneOptions],
  );

  return (
    <RedoSingleSelectDropdownInput
      options={timezoneOptions}
      optionSelected={({ item }) => onChange(item.value)}
      placeholder="Select timezone"
      selectedItem={selectedTimezone}
    />
  );
});

function convertStringToTimeOfDay(
  timeString: string | undefined,
): TimeOfDay | null {
  if (!timeString) {
    return null;
  }
  const parts = timeString.split(":").map(Number);
  return { hours: parts[0] ?? 0, minutes: parts[1] ?? 0 };
}

function convertTimeOfDayToString(timeOfDay: TimeOfDay): string {
  return `${timeOfDay.hours.toString().padStart(2, "0")}:${timeOfDay.minutes.toString().padStart(2, "0")}`;
}

function convertStringRangeToTimeOfDayRange(
  start: string | undefined,
  end: string | undefined,
): TimeOfDayRange | null {
  const startTime = convertStringToTimeOfDay(start || "09:00");
  const endTime = convertStringToTimeOfDay(end || "17:00");
  if (!startTime || !endTime) {
    return null;
  }
  return { start: startTime, end: endTime };
}

const WeeklyScheduleSection = memo(function WeeklyScheduleSection({
  condition,
  onChange,
}: {
  condition: Extract<TimeBasedConditionType, { mode: "weekly_schedule" }>;
  onChange: (updated: TimeBasedConditionType) => void;
}) {
  const schedule = useMemo(
    () => condition.schedule ?? {},
    [condition.schedule],
  );

  const handleTimezoneChange = useCallback(
    (tz: string) => {
      onChange({ ...condition, timezone: tz });
    },
    [condition, onChange],
  );

  const handleDayToggle = useCallback(
    (day: DayName, enabled: boolean) => {
      const daySchedule = schedule[day] ?? { enabled: false, timeBlocks: [] };
      const updated = {
        ...daySchedule,
        enabled,
        timeBlocks:
          enabled && daySchedule.timeBlocks.length === 0
            ? [{ start: "09:00", end: "17:00" }]
            : daySchedule.timeBlocks,
      };
      onChange({ ...condition, schedule: { ...schedule, [day]: updated } });
    },
    [condition, schedule, onChange],
  );

  const handleTimeBlockChange = useCallback(
    (day: DayName, blockIndex: number, range: TimeOfDayRange | null) => {
      const daySchedule = schedule[day];
      if (!daySchedule) {
        return;
      }
      const newBlocks = daySchedule.timeBlocks.map((b: TimeBlock, i: number) =>
        i === blockIndex
          ? {
              start: range ? convertTimeOfDayToString(range.start) : undefined,
              end: range ? convertTimeOfDayToString(range.end) : undefined,
            }
          : b,
      );
      onChange({
        ...condition,
        schedule: {
          ...schedule,
          [day]: { ...daySchedule, timeBlocks: newBlocks },
        },
      });
    },
    [condition, schedule, onChange],
  );

  const handleAddTimeBlock = useCallback(
    (day: DayName) => {
      const daySchedule = schedule[day] ?? { enabled: true, timeBlocks: [] };
      onChange({
        ...condition,
        schedule: {
          ...schedule,
          [day]: {
            ...daySchedule,
            timeBlocks: [
              ...daySchedule.timeBlocks,
              { start: "09:00", end: "17:00" },
            ],
          },
        },
      });
    },
    [condition, schedule, onChange],
  );

  const handleRemoveTimeBlock = useCallback(
    (day: DayName, blockIndex: number) => {
      const daySchedule = schedule[day];
      if (!daySchedule) {
        return;
      }
      onChange({
        ...condition,
        schedule: {
          ...schedule,
          [day]: {
            ...daySchedule,
            timeBlocks: daySchedule.timeBlocks.filter(
              (_: TimeBlock, i: number) => i !== blockIndex,
            ),
          },
        },
      });
    },
    [condition, schedule, onChange],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <TimezoneSelector
        onChange={handleTimezoneChange}
        timezone={condition.timezone}
      />
      {DAYS.map((day) => {
        const daySchedule: DaySchedule = (schedule[day] as DaySchedule) ?? {
          enabled: false,
          timeBlocks: [],
        };
        return (
          <Flex align="flex-start" dir="row" gap="sm" key={day} w="full">
            <Flex align="center" gap="sm" style={{ minWidth: 130 }}>
              <RedoToggle
                setValue={(enabled) => handleDayToggle(day, enabled)}
                size="sm"
                value={daySchedule.enabled}
              />
              <Text fontSize="sm">
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </Text>
            </Flex>
            {daySchedule.enabled && (
              <Flex dir="column" gap="xs" grow={1}>
                {daySchedule.timeBlocks.map((block: TimeBlock, i: number) => (
                  <Flex align="center" gap="sm" key={i}>
                    <RedoTimeRangePicker
                      clearIcon={null}
                      setValue={(range: TimeOfDayRange | null) =>
                        handleTimeBlockChange(day, i, range)
                      }
                      value={convertStringRangeToTimeOfDayRange(
                        block.start,
                        block.end,
                      )}
                    />
                    <RedoButton
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={() => handleRemoveTimeBlock(day, i)}
                      size="sm"
                    />
                    {i === daySchedule.timeBlocks.length - 1 && (
                      <RedoButton
                        hierarchy="tertiary"
                        IconLeading={PlusIcon}
                        onClick={() => handleAddTimeBlock(day)}
                        size="sm"
                      />
                    )}
                  </Flex>
                ))}
                {daySchedule.timeBlocks.length === 0 && (
                  <RedoButton
                    hierarchy="tertiary"
                    IconLeading={PlusIcon}
                    onClick={() => handleAddTimeBlock(day)}
                    size="sm"
                  />
                )}
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
});

const SpecificDateSection = memo(function SpecificDateSection({
  condition,
  onChange,
}: {
  condition: Extract<TimeBasedConditionType, { mode: "specific_date" }>;
  onChange: (updated: TimeBasedConditionType) => void;
}) {
  const selectedOperator = useMemo(
    () =>
      specificDateOperatorOptions.find(
        (opt) => opt.value === condition.dateOperator,
      ),
    [condition.dateOperator],
  );

  const parseDate = (
    dateStr: string | undefined,
  ): Temporal.PlainDate | undefined => {
    if (!dateStr) {
      return undefined;
    }
    try {
      return Temporal.PlainDate.from(dateStr.split("T")[0] ?? dateStr);
    } catch {
      return undefined;
    }
  };

  const parseTime = (dateStr: string | undefined): string => {
    if (!dateStr) {
      return "00:00";
    }
    const timePart = dateStr.split("T")[1];
    return timePart?.slice(0, 5) ?? "00:00";
  };

  const buildDateTime = (date: string, time: string): string => {
    return `${date}T${time}`;
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <TimezoneSelector
        onChange={(tz) => onChange({ ...condition, timezone: tz })}
        timezone={condition.timezone}
      />
      <RedoSingleSelectDropdownInput
        options={specificDateOperatorOptions}
        optionSelected={({ item }) =>
          onChange({ ...condition, dateOperator: item.value })
        }
        selectedItem={selectedOperator}
      />
      {(condition.dateOperator === SpecificDateOperator.BEFORE ||
        condition.dateOperator === SpecificDateOperator.AFTER) && (
        <Flex align="center" gap="sm">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.dateTime)}
            setDate={(date: Temporal.PlainDate) =>
              onChange({
                ...condition,
                dateTime: buildDateTime(
                  date.toString(),
                  parseTime(condition.dateTime),
                ),
              })
            }
          />
          <RedoTextInput
            inputTypeHint="time"
            placeholder="00:00"
            setValue={(time: string) =>
              onChange({
                ...condition,
                dateTime: buildDateTime(
                  parseDate(condition.dateTime)?.toString() ??
                    Temporal.Now.plainDateISO().toString(),
                  time,
                ),
              })
            }
            size="sm"
            value={parseTime(condition.dateTime)}
          />
        </Flex>
      )}
      {condition.dateOperator === SpecificDateOperator.BETWEEN && (
        <Flex align="center" gap="sm" wrap="wrap">
          <Flex align="center" gap="sm">
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.startDateTime)}
              setDate={(date: Temporal.PlainDate) =>
                onChange({
                  ...condition,
                  startDateTime: buildDateTime(
                    date.toString(),
                    parseTime(condition.startDateTime),
                  ),
                })
              }
            />
            <RedoTextInput
              inputTypeHint="time"
              placeholder="00:00"
              setValue={(time: string) =>
                onChange({
                  ...condition,
                  startDateTime: buildDateTime(
                    parseDate(condition.startDateTime)?.toString() ??
                      Temporal.Now.plainDateISO().toString(),
                    time,
                  ),
                })
              }
              size="sm"
              value={parseTime(condition.startDateTime)}
            />
          </Flex>
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          <Flex align="center" gap="sm">
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.endDateTime)}
              setDate={(date: Temporal.PlainDate) =>
                onChange({
                  ...condition,
                  endDateTime: buildDateTime(
                    date.toString(),
                    parseTime(condition.endDateTime),
                  ),
                })
              }
            />
            <RedoTextInput
              inputTypeHint="time"
              placeholder="00:00"
              setValue={(time: string) =>
                onChange({
                  ...condition,
                  endDateTime: buildDateTime(
                    parseDate(condition.endDateTime)?.toString() ??
                      Temporal.Now.plainDateISO().toString(),
                    time,
                  ),
                })
              }
              size="sm"
              value={parseTime(condition.endDateTime)}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
